export type InventoryHubLocationState = {
  returnToPreview?: boolean;
  /** Edit/receive overlay opened from invoice preview — close returns to this invoice view. */
  previewInvoiceId?: number;
  /** Pay overlay opened from bill preview — close returns to this bill view. */
  previewBillId?: number;
  transactionId?: number;
  customerId?: number;
  invoiceId?: number;
  vendorId?: number;
  billId?: number;
};

export const parseInventoryHubLocationState = (state: unknown): InventoryHubLocationState | null =>
  (state as InventoryHubLocationState | null) ?? null;

export const inventoryHubListTarget = (listSearch: string) => ({
  pathname: '/inventory-transactions' as const,
  search: listSearch,
});

export const inventoryInvoicePreviewTarget = (
  invoiceId: number,
  listSearch: string,
  transactionId?: number
) => ({
  pathname: `/inventory-transactions/view/invoice/${invoiceId}` as const,
  search: listSearch,
  state: transactionId != null ? { transactionId } : undefined,
});

export const inventoryBillPreviewTarget = (
  billId: number,
  listSearch: string,
  transactionId?: number
) => ({
  pathname: `/inventory-transactions/view/bill/${billId}` as const,
  search: listSearch,
  state: transactionId != null ? { transactionId } : undefined,
});

export const resolveInvoiceEditCloseTarget = (
  invoiceId: number | undefined,
  listSearch: string,
  navState: InventoryHubLocationState | null
) => {
  if (navState?.returnToPreview && navState.previewInvoiceId) {
    return inventoryInvoicePreviewTarget(
      navState.previewInvoiceId,
      listSearch,
      navState.transactionId
    );
  }
  if (navState?.returnToPreview && invoiceId) {
    return inventoryInvoicePreviewTarget(invoiceId, listSearch, navState.transactionId);
  }
  return inventoryHubListTarget(listSearch);
};

export const resolveReceivePaymentCloseTarget = (
  listSearch: string,
  navState: InventoryHubLocationState | null
) => {
  if (navState?.returnToPreview && navState.previewInvoiceId) {
    return inventoryInvoicePreviewTarget(
      navState.previewInvoiceId,
      listSearch,
      navState.transactionId
    );
  }
  return inventoryHubListTarget(listSearch);
};

export const resolveBillEditCloseTarget = (
  billId: number | undefined,
  listSearch: string,
  navState: InventoryHubLocationState | null
) => {
  if (navState?.returnToPreview && navState.previewBillId) {
    return inventoryBillPreviewTarget(navState.previewBillId, listSearch, navState.transactionId);
  }
  if (navState?.returnToPreview && billId) {
    return inventoryBillPreviewTarget(billId, listSearch, navState.transactionId);
  }
  return inventoryHubListTarget(listSearch);
};

export const resolvePayBillCloseTarget = (
  listSearch: string,
  navState: InventoryHubLocationState | null
) => {
  if (navState?.returnToPreview && navState.previewBillId) {
    return inventoryBillPreviewTarget(navState.previewBillId, listSearch, navState.transactionId);
  }
  return inventoryHubListTarget(listSearch);
};

export const invoiceEditFromPreviewState = (
  previewInvoiceId: number,
  transactionId?: number
): InventoryHubLocationState => ({
  returnToPreview: true,
  previewInvoiceId,
  transactionId,
});

export const receivePaymentFromPreviewState = (
  previewInvoiceId: number,
  customerId: number,
  invoiceId: number,
  transactionId?: number
): InventoryHubLocationState => ({
  returnToPreview: true,
  previewInvoiceId,
  customerId,
  invoiceId,
  transactionId,
});

export const billEditFromPreviewState = (
  previewBillId: number,
  transactionId?: number
): InventoryHubLocationState => ({
  returnToPreview: true,
  previewBillId,
  transactionId,
});

export const payBillFromPreviewState = (
  previewBillId: number,
  vendorId: number,
  billId: number,
  transactionId?: number
): InventoryHubLocationState => ({
  returnToPreview: true,
  previewBillId,
  vendorId,
  billId,
  transactionId,
});
